/**
 * TeamAgentTab - Claude Code CLI agent configuration, task queue, and usage stats
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  SmartToy as AgentIcon,
  RestartAlt as RestartIcon,
  Replay as RetryIcon,
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Terminal as TerminalIcon,
  FiberManualRecord as DotIcon,
  WarningAmberOutlined,
  OpenInNewOutlined,
  ExpandMoreOutlined,
  TuneOutlined,
  PersonOutlined,
  AccountTreeOutlined,
  IntegrationInstructionsOutlined,
  DeviceHubOutlined,
  FactCheckOutlined,
} from '@mui/icons-material';
import { agentApi, teamApi } from '@/services/api';
import SkillsPluginsTable from '@/components/team/SkillsPluginsTable';
import WorkflowsTab from '../../components/team/WorkflowsTab';
import { useAuth } from '@/context/AuthContext';
import { renderJiraWiki } from '@/utils/jiraMarkup';
import type { AgentConfigUpdate, AgentHealth, AgentSession, AgentTask, InteractiveSessionItem, JiraSpyTicket, TeamGoogleOAuthConfig } from '@/types';

const CLAUDE_MODELS = [
  { label: 'Claude Opus 4.7', id: 'claude-opus-4-7' },
  { label: 'Claude Sonnet 4.6', id: 'claude-sonnet-4-6' },
  { label: 'Claude Haiku 4.5', id: 'claude-haiku-4-5-20251001' },
  { label: 'Claude Sonnet 4.5', id: 'claude-sonnet-4-5-20250929' },
  { label: 'Claude Opus 4.5', id: 'claude-opus-4-5-20251101' },
  { label: 'Claude Sonnet 4', id: 'claude-sonnet-4-20250514' },
  { label: 'Claude Opus 4', id: 'claude-opus-4-20250514' },
];

const DEFAULT_PR_REVIEW_GUIDELINES = `- Verify the PR description clearly states the purpose and scope of the change.
- Note any backward-compatibility concerns: check whether public interfaces, exported types, CLI flags, resource schemas, or SDK method signatures have changed in a breaking way. Breaking changes require a major version bump and migration guide.
- Check for correctness: does the code do what it claims to do? Verify logic against the relevant API contract (OpenAPI spec), including error handling, response parsing, and resource lifecycle ordering (e.g. waiting for a resource to be available before dependent operations).
- Look for regressions: do any of the changed files affect other modules, commands, or resources in ways not addressed here? Pay special attention to shared utilities, base clients, and code generation templates.
- Check for security concerns: unvalidated input, exposed credentials, insecure defaults, and any token or secret committed to the diff. For IaC and SDK resources, verify that sensitive attributes are handled correctly across all affected tools.
- Flag missing or incomplete tests. For bug fixes, a regression test is required. For IaC providers and CLI tools, check that integration or acceptance tests are present or updated. A bug fix without a regression test is incomplete.
- Identify code quality issues: naming clarity, unnecessary complexity, duplicated logic, and adherence to language-specific conventions. Flag only issues that affect readability or maintainability at a practical level.`;

const DEFAULT_PR_REVIEW_OUTPUT_PROMPT = `Structure your review as:

🎯 *Purpose* — What is this PR trying to achieve? (1–3 sentences)

🔍 *What Changed* — Key files and logic modifications, in plain language.

⚠️ *Findings* — For each issue found: what the problem is, why it matters, and what you recommend to fix it. If no issues are found, state that explicitly.

💥 *Impact Assessment* — Risk level (Low / Medium / High), what areas of the system are affected.

🏁 *Verdict* — One sentence justifying the verdict, ending with exactly one of these tokens on its own line: Approve, Request Changes, or Block.`;

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const STATUS_CHIP_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  confirming: 'warning',
  pending: 'default',
  planning: 'info',
  plan_review: 'warning',
  approved: 'primary',
  implementing: 'secondary',
  answering: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const STATUS_EMOJI: Record<string, string> = {
  confirming: '❓',
  pending: '⏳',
  planning: '🤔',
  plan_review: '👀',
  approved: '✅',
  implementing: '🔨',
  answering: '💬',
  completed: '🎉',
  failed: '❌',
  cancelled: '🚫',
};

/**
 * SessionCard - renders a single active session with a live terminal view.
 */
function SessionCard({ session }: { session: AgentSession }) {
  const logEndRef = React.useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = React.useState(true);

  // Auto-scroll to the bottom whenever new output arrives
  React.useEffect(() => {
    if (autoScroll && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [session.session_log, autoScroll]);

  const elapsed = React.useMemo(() => {
    const start = new Date(session.started_at).getTime();
    const now = Date.now();
    const secs = Math.floor((now - start) / 1000);
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    const remSecs = secs % 60;
    return `${mins}m ${remSecs}s`;
  }, [session.started_at]);

  const lineCount = session.session_log?.split('\n').length ?? 0;

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: '12px !important' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DotIcon sx={{ fontSize: 12, color: 'success.main', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } } }} />
            <Typography variant="subtitle2" fontFamily="monospace">
              {session.id.slice(0, 8)}
            </Typography>
            <Chip
              label={`${STATUS_EMOJI[session.status] || ''} ${session.status}`}
              color={STATUS_CHIP_COLOR[session.status] || 'default'}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ⏱ {elapsed} · {lineCount} lines
            </Typography>
            <Tooltip title={autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}>
              <IconButton
                size="small"
                color={autoScroll ? 'primary' : 'default'}
                onClick={() => setAutoScroll((v) => !v)}
              >
                <CollapseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {session.description}
        </Typography>

        {/* Terminal output */}
        <Box
          sx={{
            bgcolor: '#0d1117',
            color: '#c9d1d9',
            borderRadius: 1,
            p: 1.5,
            maxHeight: 350,
            minHeight: 80,
            overflow: 'auto',
            fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
            fontSize: '0.75rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.1)',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { bgcolor: '#0d1117' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#30363d', borderRadius: 3 },
          }}
        >
          {session.session_log ? (
            <>
              {session.session_log}
              <Box
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 14,
                  bgcolor: '#58a6ff',
                  animation: 'blink 1s step-end infinite',
                  ml: 0.5,
                  verticalAlign: 'text-bottom',
                  '@keyframes blink': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 60, gap: 1 }}>
              <Box
                sx={{
                  width: '60%',
                  maxWidth: 200,
                  height: 3,
                  bgcolor: '#21262d',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    height: '100%',
                    width: '40%',
                    bgcolor: '#58a6ff',
                    borderRadius: 1.5,
                    animation: 'loadingSlide 1.5s ease-in-out infinite',
                    '@keyframes loadingSlide': {
                      '0%': { left: '-40%' },
                      '100%': { left: '100%' },
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#8b949e' }}>
                {session.status === 'planning'
                  ? 'Agent is analyzing the codebase and generating a plan…'
                  : session.status === 'implementing'
                    ? 'Agent is implementing changes…'
                    : session.status === 'answering'
                      ? 'Agent is preparing an answer…'
                      : 'Agent is processing…'}
              </Typography>
            </Box>
          )}
          <div ref={logEndRef} />
        </Box>
      </CardContent>
    </Card>
  );
}


export default function TeamAgentTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const { isTeamLead } = useAuth();
  const canEdit = isTeamLead(teamId!);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Config form state
  const [formData, setFormData] = useState<AgentConfigUpdate>({
    agent_provider_mode: '',
    modelhub_model: '',
    modelhub_api_url: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  // MCP token inputs (kept separate like apiKeyInput — only sent when non-empty)
  const [githubTokenInput, setGithubTokenInput] = useState('');
  const [jiraApiTokenInput, setJiraApiTokenInput] = useState('');
  const [confluenceApiTokenInput, setConfluenceApiTokenInput] = useState('');
  const [gitlabTokenInput, setGitlabTokenInput] = useState('');
  // Model Hub key: separate from formData, never stored there (follows apiKeyInput pattern)
  const [modelhubKeyInput, setModelhubKeyInput] = useState('');
  const [modelhubKeyError, setModelhubKeyError] = useState(false);
  // Integration configure dialog
  const [configDialog, setConfigDialog] = useState<'github' | 'jira' | 'confluence' | 'gitlab' | null>(null);

  // Task detail dialog
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  // Advance confirmation dialog
  const [advanceConfirmOpen, setAdvanceConfirmOpen] = useState(false);
  const [advanceTarget, setAdvanceTarget] = useState<{ taskId: string; stage: string; ticketKey: string } | null>(null);
  // Expandable log rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Decommission interactive session confirmation
  const [decommissionTarget, setDecommissionTarget] = useState<InteractiveSessionItem | null>(null);

  // Queries
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['agent-config', teamId],
    queryFn: () => agentApi.getConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['agent-tasks', teamId],
    queryFn: () => agentApi.getTasks(teamId!).then((res) => res.data),
    enabled: !!teamId,
    refetchInterval: 15000, // Auto-refresh every 15s
  });

  const { data: usage } = useQuery({
    queryKey: ['agent-usage', teamId],
    queryFn: () => agentApi.getUsage(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: health } = useQuery<AgentHealth>({
    queryKey: ['agent-health', teamId],
    queryFn: () => agentApi.getHealth(teamId!).then((res) => res.data),
    enabled: !!teamId,
    refetchInterval: 15000, // Poll health every 15s
  });

  const { data: sessions } = useQuery<AgentSession[]>({
    queryKey: ['agent-sessions', teamId],
    queryFn: () => agentApi.getSessions(teamId!).then((res) => res.data),
    enabled: !!teamId,
    refetchInterval: 3000, // Poll every 3s for live output
  });

  const { data: interactiveSessions } = useQuery<InteractiveSessionItem[]>({
    queryKey: ['interactive-sessions', teamId],
    queryFn: () => agentApi.listInteractiveSessions(teamId!),
    enabled: !!teamId && tabValue === 4,
    refetchInterval: 10000,
  });

  const decommissionMutation = useMutation({
    mutationFn: (sessionId: string) => agentApi.decommissionInteractiveSession(teamId!, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interactive-sessions', teamId] });
      setDecommissionTarget(null);
      setSnackbar({ open: true, message: 'Session decommissioned.', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to decommission session.', severity: 'error' });
    },
  });

  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: promptConfig } = useQuery({
    queryKey: ['prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: watchedTickets } = useQuery<JiraSpyTicket[]>({
    queryKey: ['jira-spy-history', teamId],
    queryFn: () => agentApi.getJiraSpyHistory(teamId!).then((res) => res.data as JiraSpyTicket[]),
    enabled: !!teamId && !!promptConfig?.enable_jira_spy,
    refetchInterval: 30000,
  });

  const { data: teamGoogleOAuth } = useQuery<TeamGoogleOAuthConfig>({
    queryKey: ['team-google-oauth', teamId],
    queryFn: () => teamApi.getGoogleOAuthConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  // Populate form when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        persona: config.persona,
        instructions: config.instructions || undefined,
        model: config.model,
        budget_limit: config.budget_limit || undefined,
        task_queue_limit: config.task_queue_limit,
        question_timeout: config.question_timeout,
        planning_timeout: config.planning_timeout,
        implementation_timeout: config.implementation_timeout,
        golden_branch: config.golden_branch,
        enable_parallel: config.enable_parallel,
        spec_driven_enabled: config.spec_driven_enabled,
        spec_review_max_attempts: config.spec_review_max_attempts,
        plan_review_enabled: config.plan_review_enabled,
        plan_review_max_attempts: config.plan_review_max_attempts,
        is_active: config.is_active,
        // MCP plain-text fields (not secrets)
        jira_base_url: config.jira_base_url || undefined,
        confluence_base_url: config.confluence_base_url || undefined,
        confluence_username: config.confluence_username || undefined,
        gitlab_url: config.gitlab_url || undefined,
        // Jira spy configuration
        jira_spy_label: config.jira_spy_label || '',
        jira_spy_exclude_statuses: config.jira_spy_exclude_statuses || 'Closed, Resolved, Fixed',
        jira_ai_execution_label: config.jira_ai_execution_label || '',
        // Model Hub / Failover
        agent_provider_mode: config.agent_provider_mode ?? 'anthropic',
        modelhub_model: config.modelhub_model ?? '',
        modelhub_api_url: config.modelhub_api_url ?? '',
        // Interactive Sessions
        interactive_sessions_enabled: config.interactive_sessions_enabled ?? false,
        interactive_idle_timeout_hours: config.interactive_idle_timeout_hours ?? 6,
        // Google Drive MCP
        gdrive_mcp_enabled: config.gdrive_mcp_enabled ?? false,
        // Google Calendar MCP
        gcal_mcp_enabled: config.gcal_mcp_enabled ?? false,
        // PR Review
        pr_review_enabled: config.pr_review_enabled ?? false,
        pr_review_mode: config.pr_review_mode ?? null,
        pr_review_guidelines: config.pr_review_guidelines ?? '',
        pr_review_output_prompt: config.pr_review_output_prompt ?? '',
        pr_review_budget_enabled: config.pr_review_budget_enabled ?? false,
        pr_review_compact: config.pr_review_compact ?? false,
      });
      setHasChanges(false);
      setApiKeyInput('');
      setGithubTokenInput('');
      setJiraApiTokenInput('');
      setConfluenceApiTokenInput('');
      setGitlabTokenInput('');
      setModelhubKeyInput('');
    }
  }, [config]);

  // Mutations
  const configMutation = useMutation({
    mutationFn: (data: AgentConfigUpdate) => agentApi.updateConfig(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-config', teamId] });
      setSnackbar({ open: true, message: 'Agent configuration saved!', severity: 'success' });
      setHasChanges(false);
      setApiKeyInput('');
      setGithubTokenInput('');
      setJiraApiTokenInput('');
      setConfluenceApiTokenInput('');
      setGitlabTokenInput('');
      setModelhubKeyInput('');
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Save failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (taskId: string) => agentApi.cancelTask(teamId!, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tasks', teamId] });
      queryClient.invalidateQueries({ queryKey: ['agent-usage', teamId] });
      setSnackbar({ open: true, message: 'Task cancelled', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Cancel failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (taskId: string) => agentApi.retryTask(teamId!, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tasks', teamId] });
      queryClient.invalidateQueries({ queryKey: ['agent-usage', teamId] });
      setSnackbar({ open: true, message: 'Task queued for retry', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Retry failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => agentApi.deleteTask(teamId!, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tasks', teamId] });
      queryClient.invalidateQueries({ queryKey: ['agent-usage', teamId] });
      setSnackbar({ open: true, message: 'Task removed', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Delete failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const restartMutation = useMutation({
    mutationFn: () => agentApi.restartRunner(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-health', teamId] });
      setSnackbar({ open: true, message: 'Restart signal sent. The agent runner will restart shortly.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `Restart failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const mcpCheckMutation = useMutation({
    mutationFn: () => agentApi.testMcpConnections(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-health', teamId] });
      setSnackbar({ open: true, message: 'MCP connection test complete — status updated.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: `MCP check failed: ${getErrorMessage(error)}`, severity: 'error' });
    },
  });

  const advanceMutation = useMutation({
    mutationFn: ({ taskId, targetStage }: { taskId: string; targetStage: string }) =>
      agentApi.advanceTask(teamId!, taskId, targetStage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-tasks', teamId] });
      setSnackbar({ open: true, message: 'Task advanced successfully', severity: 'success' });
      setSelectedTask(null);
      setTaskDialogOpen(false);
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      const msg = error?.response?.data?.detail || 'Failed to advance task';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    },
  });

  // Derived state
  const activeTaskCount = useMemo(() => {
    if (!tasks) return 0;
    const activeStatuses = ['confirming', 'pending', 'planning', 'plan_review', 'approved', 'implementing', 'answering'];
    return tasks.filter((t: AgentTask) => activeStatuses.includes(t.status)).length;
  }, [tasks]);

  const handleChange = (field: keyof AgentConfigUpdate, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (
      (formData.agent_provider_mode === 'auto' || formData.agent_provider_mode === 'modelhub') &&
      !config?.has_modelhub_key &&
      !modelhubKeyInput
    ) {
      setModelhubKeyError(true);
      return;
    }
    setModelhubKeyError(false);
    const saveData: AgentConfigUpdate = { ...formData };
    if (apiKeyInput) {
      saveData.anthropic_api_key = apiKeyInput;
    }
    saveData.agent_provider_mode = formData.agent_provider_mode;
    saveData.modelhub_model = formData.modelhub_model || null;
    saveData.modelhub_api_url = formData.modelhub_api_url || null;
    if (modelhubKeyInput) {
      saveData.modelhub_api_key = modelhubKeyInput;
    }
    // Empty string → null so DB stays NULL (= use runtime default in agent-runner)
    if (!saveData.pr_review_guidelines) saveData.pr_review_guidelines = null;
    if (!saveData.pr_review_output_prompt) saveData.pr_review_output_prompt = null;
    configMutation.mutate(saveData);
  };

  const handleCloseConfigDialog = () => {
    if (configDialog === 'github') setGithubTokenInput('');
    if (configDialog === 'jira') setJiraApiTokenInput('');
    if (configDialog === 'confluence') setConfluenceApiTokenInput('');
    if (configDialog === 'gitlab') setGitlabTokenInput('');
    setConfigDialog(null);
  };

  const handleDialogSave = (action?: { clearGitlabToken?: boolean }) => {
    const saveData: AgentConfigUpdate = {};
    if (configDialog === 'github') {
      if (githubTokenInput) saveData.github_token = githubTokenInput;
    } else if (configDialog === 'jira') {
      if (jiraApiTokenInput) saveData.jira_api_token = jiraApiTokenInput;
      if (formData.jira_base_url !== undefined) saveData.jira_base_url = formData.jira_base_url;
      if (formData.jira_spy_label !== undefined) saveData.jira_spy_label = formData.jira_spy_label;
      if (formData.jira_spy_exclude_statuses !== undefined)
        saveData.jira_spy_exclude_statuses = formData.jira_spy_exclude_statuses;
      if (formData.jira_ai_execution_label !== undefined)
        saveData.jira_ai_execution_label = formData.jira_ai_execution_label;
    } else if (configDialog === 'confluence') {
      if (confluenceApiTokenInput) saveData.confluence_api_token = confluenceApiTokenInput;
      if (formData.confluence_base_url !== undefined)
        saveData.confluence_base_url = formData.confluence_base_url;
    } else if (configDialog === 'gitlab') {
      if (action?.clearGitlabToken) {
        saveData.gitlab_token = '';
        saveData.gitlab_url = undefined;
      } else {
        if (gitlabTokenInput) saveData.gitlab_token = gitlabTokenInput;
        if (formData.gitlab_url !== undefined) saveData.gitlab_url = formData.gitlab_url;
      }
    }
    configMutation.mutate(saveData);
    setConfigDialog(null);
  };

  const handleViewTask = async (task: AgentTask) => {
    try {
      const res = await agentApi.getTask(teamId!, task.id);
      setSelectedTask(res.data);
      setTaskDialogOpen(true);
    } catch {
      setSelectedTask(task);
      setTaskDialogOpen(true);
    }
  };

  // Auto-refresh session log every 5s while task detail dialog is open
  useEffect(() => {
    if (!taskDialogOpen || !selectedTask || !teamId) return;
    const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];
    if (TERMINAL_STATUSES.includes(selectedTask.status)) return;
    const taskId = selectedTask.id;
    const id = setInterval(async () => {
      try {
        const res = await agentApi.getTask(teamId, taskId);
        setSelectedTask(res.data);
      } catch {
        // silently skip on error
      }
    }, 5000);
    return () => clearInterval(id);
  }, [taskDialogOpen, selectedTask, teamId]);

  const toggleRowExpanded = (taskId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const cardBg = 'background.paper';

  if (!teamId) return null;

  const budgetPercent =
    config?.budget_limit && config.budget_limit > 0
      ? Math.min(100, (config.budget_used / config.budget_limit) * 100)
      : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgentIcon color="primary" />
          <Typography variant="h6">Claude Code Agent</Typography>
          {config && (
            <Chip
              label={config.is_active ? 'Active' : 'Inactive'}
              color={config.is_active ? 'success' : 'default'}
              size="small"
            />
          )}
          {health && (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor:
                      health.runner_status === 'online'
                        ? 'success.main'
                        : health.runner_status === 'offline'
                          ? 'error.main'
                          : 'grey.500',
                    ml: 0.5,
                  }}
                />
              }
              label={`Runner ${health.runner_status}`}
              color={
                health.runner_status === 'online'
                  ? 'success'
                  : health.runner_status === 'offline'
                    ? 'error'
                    : 'default'
              }
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        {canEdit && tabValue === 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartIcon />}
              onClick={() => restartMutation.mutate()}
              disabled={restartMutation.isPending || health?.runner_status === 'disabled'}
            >
              {restartMutation.isPending ? 'Restarting...' : 'Restart Code'}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!hasChanges || configMutation.isPending}
            >
              {configMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        )}
      </Box>

      {hasChanges && tabValue === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have unsaved changes.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Configuration" />
          <Tab label="Skills & Plugins" />
          <Tab label="Workflows" />
          <Tab label="Task Queue" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TerminalIcon fontSize="small" />
                Sessions
                {(sessions?.length ?? 0) > 0 && (
                  <Chip label={sessions!.length} color="secondary" size="small" sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }} />
                )}
              </Box>
            }
          />
          <Tab label="Usage" />
        </Tabs>
      </Box>

      {/* Configuration Tab */}
      <TabPanel value={tabValue} index={0}>
        {configLoading ? (
          <LinearProgress />
        ) : (
          <Stack spacing={3}>
            {/* Status Banner */}
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                bgcolor: 'background.paper',
                borderLeft: '3px solid',
                borderLeftColor: health?.runner_status === 'online'
                  ? 'success.main'
                  : health?.runner_status === 'offline'
                    ? 'error.main'
                    : 'grey.500',
              }}
            >
              <FormControlLabel
                disabled={!canEdit}
                control={
                  <Switch
                    checked={formData.is_active ?? true}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Agent Active</Typography>
                    <Typography variant="body2" color="text.secondary">
                      When active, the agent runner will process queued tasks.
                    </Typography>
                  </Box>
                }
              />

              {/* Runner health indicator */}
              {health && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor:
                          health.runner_status === 'online'
                            ? 'success.main'
                            : health.runner_status === 'offline'
                              ? 'error.main'
                              : 'grey.500',
                        animation:
                          health.runner_status === 'online'
                            ? 'pulse 2s infinite'
                            : 'none',
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.5 },
                          '100%': { opacity: 1 },
                        },
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      Agent Runner:{' '}
                      {health.runner_status === 'online'
                        ? 'Online'
                        : health.runner_status === 'offline'
                          ? 'Offline'
                          : health.runner_status === 'disabled'
                            ? 'Disabled'
                            : 'Unknown'}
                    </Typography>
                  </Box>
                  {health.last_heartbeat && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                      Last heartbeat: {new Date(health.last_heartbeat).toLocaleString()}
                    </Typography>
                  )}
                  {health.runner_status === 'offline' && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', ml: 3, mt: 0.5 }}>
                      The agent runner process is not responding. Check the runner container logs.
                    </Typography>
                  )}
                  {health.provider_mode && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3, mt: 0.5 }}>
                      Provider:{' '}
                      {health.provider_mode === 'anthropic'
                        ? 'Anthropic'
                        : health.provider_mode === 'modelhub'
                          ? 'Model Hub'
                          : health.provider_mode === 'auto'
                            ? 'Auto'
                            : health.provider_mode}
                      {health.active_model ? ` (${health.active_model})` : ''}
                    </Typography>
                  )}

                  {/* MCP Service Status */}
                  {health.mcp_status && (
                    <Box sx={{ mt: 1.5, ml: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          MCP Services:
                        </Typography>
                        {canEdit && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => mcpCheckMutation.mutate()}
                            disabled={mcpCheckMutation.isPending}
                            sx={{ minWidth: 0, px: 1, py: 0, fontSize: '0.7rem', lineHeight: 1.6, textTransform: 'none' }}
                          >
                            {mcpCheckMutation.isPending ? 'Testing…' : 'Test Connection'}
                          </Button>
                        )}
                      </Box>
                      {(['jira', 'confluence', 'github', 'gitlab'] as const).map((service) => {
                        const status = health.mcp_status?.[service];
                        const serviceLabel =
                          service === 'jira'
                            ? 'JIRA'
                            : service === 'confluence'
                              ? 'Confluence'
                              : service === 'gitlab'
                                ? 'GitLab'
                                : 'GitHub';
                        const configured = status?.configured ?? false;
                        const authenticated = status?.authenticated ?? false;
                        const errorMsg = status?.error ?? '';

                        let color: string;
                        let label: string;

                        if (!configured) {
                          color = 'grey.500';
                          label = 'Not configured';
                        } else if (!authenticated) {
                          color = 'error.main';
                          label = errorMsg || 'Connection failed';
                        } else {
                          color = 'success.main';
                          label = 'Connected';
                        }

                        return (
                          <Box key={service} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="caption" color={authenticated ? 'success.main' : 'text.secondary'}>
                              {serviceLabel}: {label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>

            {/* Model & API Key */}
            <Accordion
              defaultExpanded={!config?.has_api_key}
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                bgcolor: cardBg,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Model &amp; API Key</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Autocomplete
                    freeSolo
                    options={CLAUDE_MODELS}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option.id
                    }
                    value={CLAUDE_MODELS.find((m) => m.id === formData.model) || null}
                    inputValue={formData.model || ''}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        handleChange('model', value);
                      }
                    }}
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue !== 'string') {
                        handleChange('model', newValue.id);
                      } else if (typeof newValue === 'string') {
                        handleChange('model', newValue);
                      } else {
                        handleChange('model', '');
                      }
                    }}
                    renderOption={(props, option) => {
                      const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                      return (
                        <li key={key} {...rest}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.id}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    disabled={!canEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Claude Model"
                        placeholder="claude-sonnet-4-6"
                        helperText="Pick a model or type a custom model ID"
                      />
                    )}
                  />
                  <TextField
                    label="Anthropic API Key"
                    fullWidth
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => {
                      setApiKeyInput(e.target.value);
                      setHasChanges(true);
                    }}
                    disabled={!canEdit}
                    placeholder={config?.has_api_key ? '••••••••••• (key set)' : 'sk-ant-...'}
                    helperText={
                      config?.has_api_key
                        ? 'Key set. Enter a new value to replace it.'
                        : 'Required for the agent to function.'
                    }
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Persona */}
            <Accordion
              defaultExpanded
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                bgcolor: cardBg,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Persona</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    label="Persona"
                    fullWidth
                    multiline
                    rows={2}
                    value={formData.persona || ''}
                    onChange={(e) => handleChange('persona', e.target.value)}
                    disabled={!canEdit}
                    placeholder="e.g. You are a senior Go developer specializing in microservices."
                  />
                  <TextField
                    label="Instructions"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.instructions || ''}
                    onChange={(e) => handleChange('instructions', e.target.value)}
                    disabled={!canEdit}
                    placeholder="e.g. Always write unit tests, follow Clean Architecture, use Go 1.22 features..."
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Git & Task Settings */}
            <Accordion
              defaultExpanded
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                bgcolor: cardBg,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountTreeOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Git &amp; Task Settings</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Golden Branch"
                      fullWidth
                      size="small"
                      value={formData.golden_branch || ''}
                      onChange={(e) => handleChange('golden_branch', e.target.value)}
                      disabled={!canEdit}
                      placeholder="main"
                      helperText="Source branch for new feature branches"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Task Queue Limit"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.task_queue_limit ?? 3}
                      onChange={(e) => handleChange('task_queue_limit', parseInt(e.target.value) || 1)}
                      disabled={!canEdit}
                      inputProps={{ min: 1, max: 20 }}
                      helperText="Max concurrent tasks"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Budget Limit (USD/month)"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.budget_limit ?? ''}
                      onChange={(e) => handleChange('budget_limit', e.target.value ? parseFloat(e.target.value) : null)}
                      disabled={!canEdit}
                      placeholder="No limit"
                      helperText="Max monthly spend"
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Question Timeout (seconds)"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.question_timeout ?? 600}
                      onChange={(e) => handleChange('question_timeout', parseInt(e.target.value) || 30)}
                      disabled={!canEdit}
                      inputProps={{ min: 30, max: 1800 }}
                      helperText="Timeout for question-type tasks"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Planning Timeout (seconds)"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.planning_timeout ?? 600}
                      onChange={(e) => handleChange('planning_timeout', parseInt(e.target.value) || 30)}
                      disabled={!canEdit}
                      inputProps={{ min: 30, max: 3600 }}
                      helperText="Timeout for plan generation"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Implementation Timeout (seconds)"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.implementation_timeout ?? 1800}
                      onChange={(e) => handleChange('implementation_timeout', parseInt(e.target.value) || 60)}
                      disabled={!canEdit}
                      inputProps={{ min: 60, max: 7200 }}
                      helperText="Timeout for code implementation"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Tooltip title="Allow multiple Claude Code CLI processes for a single task (e.g., frontend + backend in parallel).">
                    <FormControlLabel
                      disabled={!canEdit}
                      control={
                        <Switch
                          checked={formData.enable_parallel ?? false}
                          onChange={(e) => handleChange('enable_parallel', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Parallel Sub-Agents</Typography>}
                    />
                  </Tooltip>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Tooltip
                    title="When enabled, the agent writes a formal OpenSpec change (proposal + tasks) and has a second agent validate it before implementing. If the spec is accepted, the plan-approval step is skipped and the agent goes straight to implementation. If it can't produce a valid spec after the configured number of attempts, it falls back to today's plan-review flow."
                  >
                    <FormControlLabel
                      disabled={!canEdit}
                      control={
                        <Switch
                          checked={formData.spec_driven_enabled ?? false}
                          onChange={(e) => handleChange('spec_driven_enabled', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Spec-Driven Development (OpenSpec)</Typography>}
                    />
                  </Tooltip>
                </Box>
                {formData.spec_driven_enabled && (
                  <Box sx={{ mt: 2, maxWidth: 280 }}>
                    <TextField
                      label="Max Spec Review Attempts"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.spec_review_max_attempts ?? 2}
                      onChange={(e) =>
                        handleChange('spec_review_max_attempts', parseInt(e.target.value) || 1)
                      }
                      disabled={!canEdit}
                      inputProps={{ min: 1, max: 10 }}
                      helperText="Falls back to human plan review after this many rejected spec drafts"
                    />
                  </Box>
                )}

                {/* Plan Architect Review — for non-spec-driven path */}
                {!formData.spec_driven_enabled && (
                  <Box sx={{ mt: 2 }}>
                    <Tooltip
                      title="When enabled, an AI architect reviews the generated plan before it goes to you. If approved, implementation starts automatically. If rejected too many times, falls back to manual review."
                    >
                      <FormControlLabel
                        disabled={!canEdit}
                        control={
                          <Switch
                            checked={formData.plan_review_enabled ?? false}
                            onChange={(e) => handleChange('plan_review_enabled', e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">Plan Architect Review</Typography>}
                      />
                    </Tooltip>
                  </Box>
                )}
                {!formData.spec_driven_enabled && formData.plan_review_enabled && (
                  <Box sx={{ mt: 2, maxWidth: 280 }}>
                    <TextField
                      label="Max Plan Review Attempts"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.plan_review_max_attempts ?? 2}
                      onChange={(e) =>
                        handleChange('plan_review_max_attempts', parseInt(e.target.value) || 1)
                      }
                      disabled={!canEdit}
                      inputProps={{ min: 1, max: 10 }}
                      helperText="Falls back to human review after this many architect rejections"
                    />
                  </Box>
                )}

                {/* Interactive Sessions */}
                <Box sx={{ mt: 2 }}>
                  <Tooltip title="Allow users to start persistent Claude Code CLI sessions from Google Chat. Sessions stay alive between messages until the idle timeout expires.">
                    <FormControlLabel
                      disabled={!canEdit}
                      control={
                        <Switch
                          checked={formData.interactive_sessions_enabled ?? false}
                          onChange={(e) => handleChange('interactive_sessions_enabled', e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Interactive Sessions</Typography>}
                    />
                  </Tooltip>
                </Box>
                {formData.interactive_sessions_enabled && (
                  <Box sx={{ mt: 2, maxWidth: 280 }}>
                    <TextField
                      label="Idle Timeout (hours)"
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.interactive_idle_timeout_hours ?? 6}
                      onChange={(e) =>
                        handleChange('interactive_idle_timeout_hours', parseInt(e.target.value) || 1)
                      }
                      disabled={!canEdit}
                      inputProps={{ min: 1, max: 24 }}
                      helperText="Sessions are closed automatically after this many hours of inactivity"
                    />
                  </Box>
                )}

                {/* Enable PR Review */}
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    disabled={!canEdit}
                    control={
                      <Switch
                        checked={formData.pr_review_enabled ?? false}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          handleChange('pr_review_enabled', enabled);
                          if (enabled && !formData.pr_review_mode) {
                            handleChange('pr_review_mode', 'chat_only');
                          }
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Enable PR Review</Typography>}
                  />
                  <FormHelperText sx={{ ml: 4 }}>
                    Once saved, configure the review mode and guidelines in the PR Review section below.
                  </FormHelperText>
                </Box>

              </AccordionDetails>
            </Accordion>

            {/* Integrations Accordion */}
            <Accordion
              defaultExpanded={
                !config?.has_github_token ||
                !config?.has_jira_api_token ||
                !config?.has_gitlab_token ||
                !formData.jira_spy_label
              }
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                bgcolor: cardBg,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IntegrationInstructionsOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Integrations &amp; Credentials
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* GitHub row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    GitHub
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={config?.has_github_token ? 'success' : 'default'}
                      variant={config?.has_github_token ? 'filled' : 'outlined'}
                      label={config?.has_github_token ? 'Connected' : 'Not configured'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setConfigDialog('github')}
                      disabled={!canEdit}
                    >
                      {config?.has_github_token ? 'Edit' : 'Connect'}
                    </Button>
                  </Box>
                </Box>

                <Divider />

                {/* JIRA row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    JIRA
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={config?.has_jira_api_token ? 'success' : 'default'}
                      variant={config?.has_jira_api_token ? 'filled' : 'outlined'}
                      label={config?.has_jira_api_token ? 'Connected' : 'Not configured'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setConfigDialog('jira')}
                      disabled={!canEdit}
                    >
                      {config?.has_jira_api_token ? 'Edit' : 'Connect'}
                    </Button>
                  </Box>
                </Box>

                <Divider />

                {/* Confluence row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Confluence
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={config?.has_confluence_api_token ? 'success' : 'default'}
                      variant={config?.has_confluence_api_token ? 'filled' : 'outlined'}
                      label={config?.has_confluence_api_token ? 'Connected' : 'Not configured'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setConfigDialog('confluence')}
                      disabled={!canEdit}
                    >
                      {config?.has_confluence_api_token ? 'Edit' : 'Connect'}
                    </Button>
                  </Box>
                </Box>

                <Divider />

                {/* GitLab row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    GitLab
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={config?.has_gitlab_token ? 'success' : 'default'}
                      variant={config?.has_gitlab_token ? 'filled' : 'outlined'}
                      label={config?.has_gitlab_token ? 'Connected' : 'Not configured'}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label="Configure GitLab integration"
                      onClick={() => setConfigDialog('gitlab')}
                      disabled={!canEdit}
                    >
                      {config?.has_gitlab_token ? 'Edit' : 'Connect'}
                    </Button>
                  </Box>
                </Box>

                <Divider />

                {/* Google Drive row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Google Drive
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={teamGoogleOAuth?.has_token ? 'success' : 'default'}
                      variant={teamGoogleOAuth?.has_token ? 'filled' : 'outlined'}
                      label={teamGoogleOAuth?.has_token ? 'Connected' : 'Not connected'}
                    />
                    <Tooltip
                      title={
                        !teamGoogleOAuth?.has_token
                          ? 'Connect Google account in Settings to enable this'
                          : ''
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={formData.gdrive_mcp_enabled ?? false}
                          onChange={(e) => handleChange('gdrive_mcp_enabled', e.target.checked)}
                          disabled={!canEdit || !teamGoogleOAuth?.has_token}
                          inputProps={{ 'aria-label': 'Enable Google Drive MCP' }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
                <Divider />
                {/* Google Calendar row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>Google Calendar</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      size="small"
                      color={teamGoogleOAuth?.has_token && teamGoogleOAuth?.has_calendar_scope ? 'success' : 'default'}
                      variant={teamGoogleOAuth?.has_token && teamGoogleOAuth?.has_calendar_scope ? 'filled' : 'outlined'}
                      label={teamGoogleOAuth?.has_token && teamGoogleOAuth?.has_calendar_scope ? 'Connected' : 'Not connected'}
                    />
                    <Tooltip
                      title={
                        !(teamGoogleOAuth?.has_token && teamGoogleOAuth?.has_calendar_scope)
                          ? 'Connect Google account with Calendar access in Settings to enable this'
                          : ''
                      }
                    >
                      <span>
                        <Switch
                          size="small"
                          checked={formData.gcal_mcp_enabled ?? false}
                          onChange={(e) => handleChange('gcal_mcp_enabled', e.target.checked)}
                          disabled={!canEdit || !(teamGoogleOAuth?.has_token && teamGoogleOAuth?.has_calendar_scope)}
                          inputProps={{ 'aria-label': 'Enable Google Calendar MCP' }}
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Model Hub / Failover */}
            <Accordion
              defaultExpanded={formData.agent_provider_mode !== 'anthropic' && formData.agent_provider_mode !== undefined && formData.agent_provider_mode !== ''}
              sx={{
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceHubOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Model Hub / Failover
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <FormControl disabled={!canEdit}>
                    <FormLabel>Provider Mode</FormLabel>
                    <RadioGroup
                      value={formData.agent_provider_mode ?? 'anthropic'}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          agent_provider_mode: e.target.value,
                        }));
                        setHasChanges(true);
                      }}
                    >
                      <FormControlLabel
                        value="anthropic"
                        control={<Radio />}
                        label="Anthropic only (default)"
                      />
                      <FormControlLabel
                        value="auto"
                        control={<Radio />}
                        label="Auto-failover (Anthropic → Model Hub on overload)"
                      />
                      <FormControlLabel
                        value="modelhub"
                        control={<Radio />}
                        label="Always use Model Hub"
                      />
                    </RadioGroup>
                  </FormControl>

                  {(formData.agent_provider_mode === 'auto' ||
                    formData.agent_provider_mode === 'modelhub') && (
                    <Stack spacing={2}>
                      <Alert severity="info">
                        Model Hub sessions do not report token costs. Budget limits are not enforced
                        when using Model Hub.
                      </Alert>

                      <TextField
                        label="IONOS API Key"
                        type="password"
                        disabled={!canEdit}
                        placeholder={
                          config?.has_modelhub_key ? '••••••••  (key saved)' : 'Enter IONOS API key'
                        }
                        value={modelhubKeyInput}
                        onChange={(e) => {
                          setModelhubKeyInput(e.target.value);
                          if (modelhubKeyError) setModelhubKeyError(false);
                          setHasChanges(true);
                        }}
                        helperText={config?.has_modelhub_key ? 'Key already saved — enter new key to replace' : 'Required for Model Hub access'}
                        error={modelhubKeyError}
                      />

                      <TextField
                        label="Model"
                        disabled={!canEdit}
                        placeholder="Qwen/Qwen3-Coder-Next"
                        value={formData.modelhub_model ?? ''}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, modelhub_model: e.target.value || null }));
                          setHasChanges(true);
                        }}
                      />

                      <TextField
                        label="API URL"
                        disabled={!canEdit}
                        placeholder="https://openai.inference.de-txl.ionos.com/v1"
                        value={formData.modelhub_api_url ?? ''}
                        onChange={(e) => {
                          setFormData((prev) => ({ ...prev, modelhub_api_url: e.target.value || null }));
                          setHasChanges(true);
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* PR Review */}
            {formData.pr_review_enabled && (
              <Accordion
                defaultExpanded
                sx={{
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  bgcolor: cardBg,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FactCheckOutlined sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={600}>PR Review</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Review Mode</Typography>
                      <Stack spacing={1}>
                        <FormControlLabel
                          disabled={!canEdit}
                          control={
                            <Switch
                              checked={formData.pr_review_mode === 'chat_only'}
                              disabled={
                                !canEdit ||
                                formData.pr_review_mode === 'chat_and_comments' ||
                                formData.pr_review_mode === 'full_automation'
                              }
                              onChange={() => handleChange('pr_review_mode', 'chat_only')}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Chat summary only</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Post a review summary as a thread in the chat
                                {(formData.pr_review_mode === 'chat_and_comments' ||
                                  formData.pr_review_mode === 'full_automation') &&
                                  ' (included in this mode)'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          disabled={!canEdit}
                          control={
                            <Switch
                              checked={formData.pr_review_mode === 'chat_and_comments'}
                              disabled={!canEdit || formData.pr_review_mode === 'full_automation'}
                              onChange={() => handleChange('pr_review_mode', 'chat_and_comments')}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Chat + inline PR comments</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Also post inline comments directly on the PR (GitHub only)
                                {formData.pr_review_mode === 'full_automation' &&
                                  ' (included in this mode)'}
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          disabled={!canEdit}
                          control={
                            <Switch
                              checked={formData.pr_review_mode === 'full_automation'}
                              disabled={!canEdit}
                              onChange={() => handleChange('pr_review_mode', 'full_automation')}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Full automation</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Apply suggested fixes and push them to the branch — High token usage
                              </Typography>
                            </Box>
                          }
                        />
                      </Stack>
                    </Box>

                    <Box>
                      <FormControlLabel
                        disabled={!canEdit}
                        control={
                          <Switch
                            checked={formData.pr_review_budget_enabled ?? false}
                            onChange={(e) => handleChange('pr_review_budget_enabled', e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">Enforce budget limit on PR reviews</Typography>}
                      />
                      <FormHelperText sx={{ ml: 4 }}>
                        PR review costs are always tracked. Enable this to also block new reviews when
                        the team budget is exhausted.
                      </FormHelperText>
                    </Box>

                    <Box>
                      <FormControlLabel
                        disabled={!canEdit}
                        control={
                          <Switch
                            checked={formData.pr_review_compact ?? false}
                            onChange={(e) => handleChange('pr_review_compact', e.target.checked)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">Compact card mode</Typography>}
                      />
                      <FormHelperText sx={{ ml: 4 }}>
                        Show only a brief summary of each section in Google Chat. A &lsquo;Full Review&rsquo; button
                        is always included.
                      </FormHelperText>
                    </Box>

                    <Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                      >
                        <Typography variant="subtitle2">Review Guidelines</Typography>
                        <Button
                          size="small"
                          disabled={!canEdit}
                          onClick={() => handleChange('pr_review_guidelines', DEFAULT_PR_REVIEW_GUIDELINES)}
                        >
                          Reset to default
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={formData.pr_review_guidelines ?? ''}
                        onChange={(e) => handleChange('pr_review_guidelines', e.target.value)}
                        disabled={!canEdit}
                        placeholder={DEFAULT_PR_REVIEW_GUIDELINES}
                        helperText="How should the agent evaluate PRs? (team-specific rules, coding standards, what to prioritise)"
                      />
                    </Box>

                    <Box>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                      >
                        <Typography variant="subtitle2">Output Format</Typography>
                        <Button
                          size="small"
                          disabled={!canEdit}
                          onClick={() =>
                            handleChange('pr_review_output_prompt', DEFAULT_PR_REVIEW_OUTPUT_PROMPT)
                          }
                        >
                          Reset to default
                        </Button>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={formData.pr_review_output_prompt ?? ''}
                        onChange={(e) => handleChange('pr_review_output_prompt', e.target.value)}
                        disabled={!canEdit}
                        placeholder={DEFAULT_PR_REVIEW_OUTPUT_PROMPT}
                        helperText="How should the review be structured and formatted?"
                      />
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        )}

        {/* GitHub Configure Dialog */}
        <Dialog
          open={configDialog === 'github'}
          onClose={handleCloseConfigDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Configure GitHub</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="GitHub Token"
                fullWidth
                type="password"
                value={githubTokenInput}
                onChange={(e) => setGithubTokenInput(e.target.value)}
                disabled={!canEdit}
                placeholder={config?.has_github_token ? '••••••••••• (token set)' : 'ghp_...'}
                helperText={
                  config?.has_github_token
                    ? 'Leave blank to keep existing token.'
                    : 'Personal access token for repo access & PR creation.'
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfigDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => handleDialogSave()}
              disabled={!canEdit || configMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* JIRA Configure Dialog */}
        <Dialog
          open={configDialog === 'jira'}
          onClose={handleCloseConfigDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Configure JIRA</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.5}
                color="text.secondary"
              >
                Connection
              </Typography>
              <TextField
                label="JIRA Base URL"
                fullWidth
                size="small"
                value={formData.jira_base_url || ''}
                onChange={(e) => handleChange('jira_base_url', e.target.value || null)}
                disabled={!canEdit}
                placeholder="https://yourteam.atlassian.net"
                helperText="Leave empty for default"
              />
              <TextField
                label="JIRA Personal Access Token"
                fullWidth
                type="password"
                value={jiraApiTokenInput}
                onChange={(e) => setJiraApiTokenInput(e.target.value)}
                disabled={!canEdit}
                placeholder={config?.has_jira_api_token ? '••••••••••• (token set)' : 'Optional'}
                helperText={
                  config?.has_jira_api_token
                    ? 'Leave blank to keep existing token.'
                    : 'Personal Access Token (PAT) from JIRA'
                }
              />
              <Divider />
              <Typography
                variant="caption"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.5}
                color="text.secondary"
              >
                Auto-Pickup — AI Refinement
              </Typography>
              <TextField
                label="Label to monitor"
                fullWidth
                size="small"
                value={formData.jira_spy_label || ''}
                onChange={(e) => handleChange('jira_spy_label', e.target.value)}
                disabled={!canEdit}
                helperText={`Project: ${team?.jira_project_key || 'not configured'}`}
              />
              <TextField
                label="Exclude statuses"
                fullWidth
                size="small"
                value={formData.jira_spy_exclude_statuses || 'Closed, Resolved, Fixed'}
                onChange={(e) => handleChange('jira_spy_exclude_statuses', e.target.value)}
                disabled={!canEdit}
                helperText="Comma-separated statuses to skip"
              />
              {promptConfig !== undefined && (
                <Box>
                  {!promptConfig?.enable_jira_spy && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Disabled — enable in Team Settings → Features"
                    />
                  )}
                  {promptConfig?.enable_jira_spy && team?.jira_project_key && (
                    <Chip
                      size="small"
                      color="success"
                      label={`Monitoring ${team.jira_project_key}`}
                    />
                  )}
                  {promptConfig?.enable_jira_spy && !team?.jira_project_key && (
                    <Chip
                      size="small"
                      color="error"
                      label="Set project key in Team Settings → General"
                    />
                  )}
                </Box>
              )}
              <Divider />
              <Typography
                variant="caption"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.5}
                color="text.secondary"
              >
                Auto-Pickup — AI Execution
              </Typography>
              <TextField
                label="Label to monitor"
                fullWidth
                size="small"
                value={formData.jira_ai_execution_label || ''}
                onChange={(e) => handleChange('jira_ai_execution_label', e.target.value)}
                disabled={!canEdit}
                helperText={`Project: ${team?.jira_project_key || 'not configured'} — independent of the JIRA AI Refinement label above`}
              />
              {promptConfig !== undefined && (
                <Box>
                  {!promptConfig?.enable_jira_ai_execution && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Disabled — enable in Team Settings → Features"
                    />
                  )}
                  {promptConfig?.enable_jira_ai_execution && !formData.spec_driven_enabled && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Enabled but Spec-Driven mode is off — tickets won't be picked up"
                    />
                  )}
                  {promptConfig?.enable_jira_ai_execution &&
                    formData.spec_driven_enabled &&
                    team?.jira_project_key && (
                      <Chip
                        size="small"
                        color="success"
                        label={`Monitoring ${team.jira_project_key}`}
                      />
                    )}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfigDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => handleDialogSave()}
              disabled={!canEdit || configMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confluence Configure Dialog */}
        <Dialog
          open={configDialog === 'confluence'}
          onClose={handleCloseConfigDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Configure Confluence</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Confluence Base URL"
                fullWidth
                size="small"
                value={formData.confluence_base_url || ''}
                onChange={(e) => handleChange('confluence_base_url', e.target.value || null)}
                disabled={!canEdit}
                placeholder="https://yourteam.atlassian.net/wiki"
                helperText="Leave empty for default"
              />
              <TextField
                label="Confluence Personal Access Token"
                fullWidth
                type="password"
                value={confluenceApiTokenInput}
                onChange={(e) => setConfluenceApiTokenInput(e.target.value)}
                disabled={!canEdit}
                placeholder={
                  config?.has_confluence_api_token ? '••••••••••• (token set)' : 'Optional'
                }
                helperText={
                  config?.has_confluence_api_token
                    ? 'Leave blank to keep existing token.'
                    : 'Personal Access Token (PAT) from Confluence'
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfigDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => handleDialogSave()}
              disabled={!canEdit || configMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* GitLab Configure Dialog */}
        <Dialog
          open={configDialog === 'gitlab'}
          onClose={handleCloseConfigDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby="gitlab-dialog-title"
        >
          <DialogTitle id="gitlab-dialog-title">Configure GitLab</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="GitLab URL"
                fullWidth
                size="small"
                value={formData.gitlab_url || ''}
                onChange={(e) => handleChange('gitlab_url', e.target.value || undefined)}
                disabled={!canEdit}
                placeholder="https://gitlab.com"
                helperText="Leave empty to use gitlab.com"
              />
              <TextField
                label="Personal Access Token"
                fullWidth
                type="password"
                value={gitlabTokenInput}
                onChange={(e) => setGitlabTokenInput(e.target.value)}
                disabled={!canEdit}
                placeholder={config?.has_gitlab_token ? '••••••••' : 'glpat-...'}
                helperText={
                  config?.has_gitlab_token
                    ? 'Leave blank to keep existing token. api scope (required for MR creation)'
                    : 'api scope (required for MR creation)'
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Box>
              {config?.has_gitlab_token && (
                <Button
                  color="error"
                  aria-label="Disconnect GitLab"
                  onClick={() => handleDialogSave({ clearGitlabToken: true })}
                  disabled={!canEdit || configMutation.isPending}
                >
                  Disconnect GitLab
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                aria-label="Cancel GitLab configuration"
                onClick={handleCloseConfigDialog}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                aria-label="Save GitLab configuration"
                onClick={() => handleDialogSave()}
                disabled={!canEdit || configMutation.isPending}
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Skills & Plugins Tab */}
      <TabPanel value={tabValue} index={1}>
        <SkillsPluginsTable
          teamId={teamId!}
          canEdit={canEdit}
          onNotify={(message, severity) => setSnackbar({ open: true, message, severity })}
        />
      </TabPanel>

      {/* Workflows Tab */}
      <TabPanel value={tabValue} index={2}>
        <WorkflowsTab teamId={teamId!} />
      </TabPanel>

      {/* Task Queue Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Task Queue
            </Typography>
            {config && (
              <Tooltip title={`${activeTaskCount} tasks running, ${config.task_queue_limit} maximum allowed`}>
                <Chip
                  label={`${activeTaskCount} / ${config.task_queue_limit} active`}
                  color={activeTaskCount >= config.task_queue_limit ? 'warning' : 'default'}
                  size="small"
                  variant="outlined"
                  icon={activeTaskCount >= config.task_queue_limit ? <WarningAmberOutlined /> : undefined}
                  sx={{ ml: 1 }}
                />
              </Tooltip>
            )}
          </Box>
          <IconButton
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['agent-tasks', teamId] });
            }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {tasksLoading ? (
          <LinearProgress />
        ) : !tasks || tasks.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <AgentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No agent tasks yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use <code>!agent &lt;task description&gt;</code> in Google Chat to create a task.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>ID</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Branch</TableCell>
                  <TableCell>PR</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => {
                  const isExpanded = expandedRows.has(task.id);
                  const hasOutput = !!task.last_output;
                  return (
                    <React.Fragment key={task.id}>
                      <TableRow
                        hover
                        sx={{ '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                      >
                        <TableCell padding="checkbox">
                          {hasOutput && (
                            <IconButton size="small" onClick={() => toggleRowExpanded(task.id)}>
                              {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {task.id.slice(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            {task.task_type === 'question' && (
                              <Chip label="Q" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                            )}
                            {task.task_type === 'jira_refine' && (
                              <Chip label="Refine" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                            )}
                            {task.task_type === 'jira_prep_draft' && (
                              <Chip label="Draft" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                            )}
                            {task.jira_ticket_key && (
                              <Chip
                                label={task.jira_ticket_key}
                                size="small"
                                color="info"
                                variant="outlined"
                                component="a"
                                href={task.jira_ticket_url || '#'}
                                target="_blank"
                                rel="noopener"
                                clickable
                                icon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
                                sx={{ mr: 1, height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                              />
                            )}
                            <Typography variant="body2" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {task.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={`${STATUS_EMOJI[task.status] || ''} ${task.status}`}
                              color={STATUS_CHIP_COLOR[task.status] || 'default'}
                              size="small"
                              variant="outlined"
                            />
                            {task.cost_usd != null && (
                              <Chip
                                label={`$${task.cost_usd.toFixed(4)}`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {task.branch_name ? (
                            <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                              {task.branch_name}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {task.pr_url ? (
                            <a href={task.pr_url} target="_blank" rel="noopener noreferrer">
                              PR
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {task.cost_usd != null ? `$${task.cost_usd.toFixed(4)}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize="0.75rem">
                            {new Date(task.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="View Full Log">
                              <IconButton size="small" onClick={() => handleViewTask(task)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!['completed', 'cancelled', 'failed'].includes(task.status) && canEdit && (
                              <Tooltip title="Cancel Task">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => cancelMutation.mutate(task.id)}
                                  disabled={cancelMutation.isPending}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {task.status === 'failed' && canEdit && (
                              <Tooltip title="Retry Task">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => retryMutation.mutate(task.id)}
                                  disabled={retryMutation.isPending}
                                >
                                  <RetryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {['failed', 'cancelled', 'completed'].includes(task.status) && canEdit && (
                              <Tooltip title="Remove Task">
                                <IconButton
                                  size="small"
                                  color="default"
                                  onClick={() => deleteMutation.mutate(task.id)}
                                  disabled={deleteMutation.isPending}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                      {/* Expandable log summary row */}
                      {hasOutput && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  my: 1,
                                  p: 1.5,
                                  bgcolor: '#1e1e1e',
                                  color: '#d4d4d4',
                                  borderRadius: 1,
                                  fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                                  fontSize: '0.75rem',
                                  lineHeight: 1.6,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: 200,
                                  overflow: 'auto',
                                }}
                              >
                                {task.last_output}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Watched Tickets Section */}
        <Box sx={{ mt: 3 }}>
          {promptConfig?.enable_jira_spy ? (
            watchedTickets && watchedTickets.length > 0 ? (
              <Accordion defaultExpanded={watchedTickets.length <= 5}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Watched Tickets ({watchedTickets.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Stage</TableCell>
                        <TableCell>Task Status</TableCell>
                        <TableCell>Processed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {watchedTickets.map((ticket) => (
                        <TableRow key={ticket.ticket_key}>
                          <TableCell>
                            {ticket.ticket_url ? (
                              <MuiLink href={ticket.ticket_url} target="_blank" rel="noopener" underline="hover">
                                {ticket.ticket_key}
                              </MuiLink>
                            ) : (
                              ticket.ticket_key
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(ticket.last_action_label || 'unknown').replace('ai_', '')}
                              size="small"
                              color={
                                ticket.last_action_label === 'ai_refine' ? 'info' :
                                ticket.last_action_label === 'ai_prep_draft' ? 'warning' :
                                ticket.last_action_label === 'ai_ready' ? 'success' :
                                ticket.last_action_label === 'ai_execute' ? 'secondary' :
                                'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {ticket.task_status ? (
                              <Chip label={ticket.task_status} size="small" variant="outlined" />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {ticket.processed_at ? new Date(ticket.processed_at).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ) : null
          ) : (
            <Alert severity="info" variant="outlined">
              Enable JIRA Auto-Pickup in Team Settings to track tickets here.
            </Alert>
          )}
        </Box>
      </TabPanel>

      {/* Sessions Tab — live CLI output from active tasks + interactive sessions */}
      <TabPanel value={tabValue} index={4}>
        {/* --- Task Sessions --- */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Task Sessions
          </Typography>
          <IconButton
            onClick={() => queryClient.invalidateQueries({ queryKey: ['agent-sessions', teamId] })}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {!sessions || sessions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 3 }}>
            <TerminalIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No active sessions.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sessions appear here when the agent runner is executing a task (planning or implementing).
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2} sx={{ mb: 3 }}>
            {sessions.map((session) => (
              <SessionCard key={session.id} session={session} />
            ))}
          </Stack>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* --- Interactive Sessions --- */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Interactive Sessions
          </Typography>
          <IconButton
            onClick={() => queryClient.invalidateQueries({ queryKey: ['interactive-sessions', teamId] })}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {!interactiveSessions || interactiveSessions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No interactive sessions found.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={1}>
            {interactiveSessions.map((s) => {
              const ageMs = Date.now() - new Date(s.created_at).getTime();
              const ageMinutes = Math.floor(ageMs / 60000);
              const ageDisplay =
                ageMinutes < 60
                  ? `${ageMinutes}m`
                  : `${Math.floor(ageMinutes / 60)}h ${ageMinutes % 60}m`;
              const isActive = s.status === 'starting' || s.status === 'active';
              return (
                <Paper
                  key={s.id}
                  variant="outlined"
                  sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {s.thread_name || s.id}
                    </Typography>
                    {s.created_by && (
                      <Typography variant="caption" color="text.secondary">
                        Started by {s.created_by}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={s.status}
                    size="small"
                    color={
                      isActive ? 'success' : s.status === 'expired' ? 'warning' : 'default'
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Age: {ageDisplay}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {s.turn_count} turns · ${s.total_cost_usd.toFixed(4)}
                  </Typography>
                  <Tooltip title="Open Console">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!isActive}
                        onClick={() =>
                          window.open(
                            `/sessions/${s.id}/console`,
                            '_blank'
                          )
                        }
                      >
                        <OpenInNewOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setDecommissionTarget(s)}
                    disabled={!isActive}
                  >
                    Decommission
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        )}
      </TabPanel>

      {/* Decommission confirmation dialog */}
      <Dialog open={!!decommissionTarget} onClose={() => setDecommissionTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Decommission Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Stop interactive session{' '}
            <strong>{decommissionTarget?.thread_name || decommissionTarget?.id}</strong>?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecommissionTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => decommissionTarget && decommissionMutation.mutate(decommissionTarget.id)}
            disabled={decommissionMutation.isPending}
          >
            Decommission
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage Tab */}
      <TabPanel value={tabValue} index={5}>
        <Stack spacing={3}>
          {/* Budget Card — total cost summary is authoritative; do NOT recompute from cost_items */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Budget
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                ${usage?.total_cost.toFixed(4) || '0.0000'}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  all-time spend
                </Typography>
              </Typography>
              {usage?.budget_limit ? (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      ${usage.budget_used.toFixed(2)} / ${usage.budget_limit.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {budgetPercent.toFixed(0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={budgetPercent}
                    color={budgetPercent > 80 ? 'error' : budgetPercent > 50 ? 'warning' : 'primary'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No budget limit set.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Task Stats Card */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Task Statistics
              </Typography>
              <Typography variant="h4" sx={{ mb: 2 }}>
                {usage?.total_tasks || 0} <Typography component="span" variant="body1" color="text.secondary">total tasks</Typography>
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(usage?.tasks_by_status || {}).map(([status, count]) => (
                  <Grid item xs={6} sm={3} key={status}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="h6">{count}</Typography>
                      <Chip
                        label={`${STATUS_EMOJI[status] || ''} ${status}`}
                        color={STATUS_CHIP_COLOR[status] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Cost Detail Card */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Cost Detail
              </Typography>
              {!usage?.cost_items || usage.cost_items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No cost data recorded yet.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Task Title</TableCell>
                          <TableCell>Model</TableCell>
                          <TableCell>Cost (USD)</TableCell>
                          <TableCell>Completed At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usage.cost_items.map((item) => (
                          <TableRow key={item.task_id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                                {item.model ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {item.cost_usd != null ? `$${item.cost_usd.toFixed(4)}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                                {item.completed_at ? new Date(item.completed_at).toLocaleString() : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {usage.cost_items.length === 500 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Showing last 500 cost entries. Total cost above reflects all-time spend.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>

      {/* Task Detail Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Task Details — {selectedTask?.id.slice(0, 8)}
        </DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{selectedTask.description}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={`${STATUS_EMOJI[selectedTask.status] || ''} ${selectedTask.status}`}
                  color={STATUS_CHIP_COLOR[selectedTask.status] || 'default'}
                  size="small"
                />
              </Box>
              {selectedTask.status === 'failed' && selectedTask.error_info && (
                <Alert
                  severity="error"
                  variant="outlined"
                  action={
                    selectedTask.error_info.retryable && canEdit ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          retryMutation.mutate(selectedTask.id);
                          setTaskDialogOpen(false);
                          setSelectedTask(null);
                        }}
                      >
                        Retry
                      </Button>
                    ) : undefined
                  }
                >
                  <AlertTitle>{selectedTask.error_info.summary}</AlertTitle>
                  {selectedTask.error_info.suggestion}
                  {!selectedTask.error_info.retryable && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      Manual intervention required
                    </Typography>
                  )}
                </Alert>
              )}
              {selectedTask.status === 'failed' && !selectedTask.error_info && selectedTask.session_log && (
                <Alert severity="error" variant="outlined">
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTask.session_log.split('\n').slice(0, 3).join('\n')}
                  </Typography>
                </Alert>
              )}
              {selectedTask.plan_text && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Implementation Plan
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                    {renderJiraWiki(selectedTask.plan_text)}
                  </Paper>
                </>
              )}
              {selectedTask.status === 'completed' && selectedTask.jira_ticket_key && canEdit && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {selectedTask.task_type === 'jira_refine' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => advanceMutation.mutate({ taskId: selectedTask.id, targetStage: 'prep_draft' })}
                      disabled={advanceMutation.isPending}
                    >
                      Advance to Draft
                    </Button>
                  )}
                  {selectedTask.task_type === 'jira_prep_draft' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setAdvanceTarget({ taskId: selectedTask.id, stage: 'execute', ticketKey: selectedTask.jira_ticket_key! });
                        setTaskDialogOpen(false);
                        setAdvanceConfirmOpen(true);
                      }}
                      disabled={advanceMutation.isPending}
                    >
                      Approve &amp; Execute
                    </Button>
                  )}
                </Box>
              )}
              {selectedTask.revision_notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Revision Notes</Typography>
                  <Typography variant="body2">{selectedTask.revision_notes}</Typography>
                </Box>
              )}
              {selectedTask.branch_name && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Branch</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedTask.branch_name}</Typography>
                </Box>
              )}
              {selectedTask.pr_url && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Pull Request</Typography>
                  <a href={selectedTask.pr_url} target="_blank" rel="noopener noreferrer">{selectedTask.pr_url}</a>
                </Box>
              )}
              {selectedTask.session_log && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Session Log (Claude Code CLI Output)
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      maxHeight: 450,
                      overflow: 'auto',
                      bgcolor: '#1e1e1e',
                      color: '#d4d4d4',
                      borderRadius: 1,
                      fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                      fontSize: '0.75rem',
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-track': { bgcolor: '#1e1e1e' },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#555', borderRadius: 4 },
                    }}
                  >
                    {selectedTask.session_log}
                  </Box>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Timestamps</Typography>
                <Typography variant="body2">
                  Created: {new Date(selectedTask.created_at).toLocaleString()}
                </Typography>
                {selectedTask.completed_at && (
                  <Typography variant="body2">
                    Completed: {new Date(selectedTask.completed_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
              {selectedTask.cost_usd != null && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Cost</Typography>
                  <Typography variant="body2">${selectedTask.cost_usd.toFixed(4)}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Advance Confirmation Dialog */}
      <Dialog open={advanceConfirmOpen} onClose={() => setAdvanceConfirmOpen(false)}>
        <DialogTitle>Start Implementation?</DialogTitle>
        <DialogContent>
          <Typography>
            This will start implementation of <strong>{advanceTarget?.ticketKey}</strong>.
            The agent will create a branch and pull request. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (advanceTarget) {
                advanceMutation.mutate({ taskId: advanceTarget.taskId, targetStage: advanceTarget.stage });
              }
              setAdvanceConfirmOpen(false);
            }}
          >
            Start Execution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

